import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SubjectsPanel from "@/components/schedule/SubjectsPanel";
import RulesPanel from "@/components/schedule/RulesPanel";
import ExceptionsPanel from "@/components/schedule/ExceptionsPanel";

const TABS = [
  { id: "subjects", label: "Subjects" },
  { id: "rules", label: "Weekly rules" },
  { id: "exceptions", label: "Exceptions" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function Schedule() {
  const [tab, setTab] = useState<TabId>("subjects");

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="relative pb-4">
        <h1 className="text-2xl sm:text-3xl font-light tracking-[-0.05em] text-[#222] dark:text-[#ededeb]">
          Schedule
        </h1>
        <p className="mt-1 font-mono text-xs opacity-60 text-neutral-600 dark:text-neutral-400">
          Subjects, the recurring weekly timetable, and one-off changes to it.
        </p>
        <div className="meniscus absolute bottom-0 left-0 w-full h-[1px] opacity-40 pointer-events-none" />
      </div>

      <div
        role="tablist"
        className="relative flex items-center gap-1.5 pb-2 overflow-x-auto"
      >
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={[
              "relative cursor-pointer rounded-xl px-3.5 py-1.5 text-xs font-mono transition-colors duration-150",
              tab === item.id
                ? "font-semibold text-[#222] dark:text-[#ededeb]"
                : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100",
            ].join(" ")}
          >
            {tab === item.id && (
              <motion.span
                layoutId="schedule-tab-pill"
                className="absolute inset-0 rounded-xl bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                transition={{ type: "spring", stiffness: 450, damping: 35 }}
              />
            )}
            <span className="relative z-10">{item.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          {tab === "subjects" && <SubjectsPanel />}
          {tab === "rules" && <RulesPanel />}
          {tab === "exceptions" && <ExceptionsPanel />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
